import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import seemelessImage from '../assets/images/seemeless.jpg';
import { 
  Database, 
  FileText, 
  Zap, 
  Shield, 
  Cloud, 
  CheckCircle,
  ArrowRight,
  Clock,
  Lock,
  BarChart3,
  Cpu,
  Globe
} from 'lucide-react';

const TECH_IMAGE = seemelessImage;
const AI_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/vvnjwp1b_abstract-polygonal-head-outline-on-dark-backdrop-w-2025-10-15-01-46-07-utc.jpg";
const AI_BUSINESS_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg";

const platforms = [
  {
    id: 'sclin-edc',
    name: 'Sclin EDC',
    tagline: 'Next-Generation Electronic Data Capture',
    description: 'Our proprietary Sclin EDC platform revolutionizes clinical data collection with intelligent form design, real-time validation, and seamless integration capabilities.',
    icon: Database,
    stats: [
      { value: '40%', label: 'Faster Build Time' },
      { value: '99.9%', label: 'Uptime' },
      { value: '50+', label: 'Integrations' }
    ],
    features: [
      { icon: Zap, title: 'Rapid Database Design', description: 'Intuitive drag-and-drop interface with pre-built templates' },
      { icon: Shield, title: '21 CFR Part 11 Compliant', description: 'Full regulatory compliance with audit trails' },
      { icon: BarChart3, title: 'Real-Time Analytics', description: 'Live dashboards and automated data quality monitoring' },
      { icon: Globe, title: 'Multi-Language Support', description: 'Support for global trials with localized interfaces' },
      { icon: Lock, title: 'Advanced Security', description: 'Role-based access control and data encryption' },
      { icon: Cpu, title: 'AI-Powered Validation', description: 'Intelligent edit checks and discrepancy detection' }
    ]
  },
  {
    id: 'docudefender',
    name: 'DocuDefender',
    tagline: 'AI-Powered eTMF System',
    description: 'DocuDefender transforms Trial Master File management with artificial intelligence, ensuring inspection-readiness and eliminating manual document handling.',
    icon: FileText,
    stats: [
      { value: '80%', label: 'Time Saved' },
      { value: '100%', label: 'Audit Ready' },
      { value: '24/7', label: 'Access' }
    ],
    features: [
      { icon: Cpu, title: 'Automated Document Indexing', description: 'AI-powered classification and metadata extraction' },
      { icon: Clock, title: 'Real-Time Audit Readiness', description: 'Continuous completeness monitoring and gap analysis' },
      { icon: Shield, title: 'Risk Detection', description: 'Proactive identification of compliance risks' },
      { icon: Database, title: 'CTMS & EDC Integration', description: 'Seamless connectivity with clinical systems' },
      { icon: Cloud, title: 'Secure Cloud Storage', description: 'SOC 2 compliant infrastructure with global access' },
      { icon: Lock, title: 'Version Control', description: 'Complete document history and change tracking' }
    ]
  }
];

const capabilities = [
  { title: 'Machine Learning', description: 'Predictive analytics for enrollment, safety signals, and trial outcomes' },
  { title: 'Natural Language Processing', description: 'Automated review of clinical documents and adverse event narratives' },
  { title: 'Computer Vision', description: 'Image analysis for medical imaging trials and document processing' },
  { title: 'Predictive Modeling', description: 'Risk-based monitoring and resource optimization algorithms' }
];

export const Technology = ({ onDemoClick }) => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="technology-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">Technology</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="technology-title">
                AI-Powered Clinical <span className="text-gradient">Innovation</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg">
                Our proprietary technology platforms leverage artificial intelligence and advanced 
                analytics to streamline clinical operations, reduce timelines, and improve data quality.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      {platforms.map((platform, index) => (
        <section 
          key={platform.id}
          id={platform.id}
          className={`py-20 ${index % 2 === 0 ? 'section-surface' : 'section-dark'} border-y border-[#2E3446] scroll-mt-24`}
          data-testid={`platform-${platform.id}`}
        >
          <div className="w-full px-6 sm:px-12 lg:px-20">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 0 ? '' : 'lg:order-2'}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="icon-box w-16 h-16">
                    <platform.icon className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-outfit font-bold text-silver-shine">{platform.name}</h2>
                    <span className="text-[#8F262B] text-sm font-medium">{platform.tagline}</span>
                  </div>
                </div>

                <p className="text-[#C9C9CD] text-lg mb-8">{platform.description}</p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {platform.stats.map((stat) => (
                    <div key={stat.label} className="card-gradient p-4 text-center">
                      <div className="stat-value text-2xl mb-1">{stat.value}</div>
                      <div className="text-xs text-[#9F999F] uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={onDemoClick}
                  className="btn-gradient inline-flex items-center gap-2 px-6 py-3"
                  data-testid={`demo-${platform.id}-btn`}
                >
                  Request Demo
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 0 ? '' : 'lg:order-1'}
              >
                <div className="grid gap-3">
                  {platform.features.map((feature, featureIndex) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: featureIndex * 0.05 }}
                      className="card-dark p-4 flex items-start gap-4"
                    >
                      <div className="icon-box w-10 h-10 flex-shrink-0">
                        <feature.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-silver-shine font-medium mb-1">{feature.title}</h4>
                        <p className="text-[#9F999F] text-sm">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* AI Capabilities */}
      <section id="ai" className="py-20 scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="text-left">
              <span className="section-label">AI Capabilities</span>
              <h2 className="section-title mt-4">Advanced <span className="text-gradient">Analytics</span></h2>
              <p className="text-[#C9C9CD] mt-4">
                Harnessing the power of artificial intelligence across clinical operations
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden"
            >
              <img 
                src={AI_IMAGE} 
                alt="AI Technology" 
                className="w-full h-80 object-cover rounded-2xl border border-[#2E3446]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090B17]/60 via-transparent to-transparent" />
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-gradient p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="icon-box w-12 h-12 flex-shrink-0">
                    <Cpu className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-silver-shine font-semibold mb-2">{cap.title}</h3>
                    <p className="text-[#9F999F] text-sm">{cap.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Integration</span>
              <h2 className="section-title mt-4 mb-6">
                Seamless <span className="text-gradient">Connectivity</span>
              </h2>
              <p className="text-[#C9C9CD] mb-8">
                Our platforms integrate with your existing clinical infrastructure, including 
                CTMS, CDMS, safety databases, and regulatory systems for a unified workflow.
              </p>

              <div className="space-y-4">
                {['CTMS Integration', 'CDISC Compliance', 'HL7/FHIR Support', 'API Access'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#8F262B]" />
                    <span className="text-[#C9C9CD]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden glow-subtle">
                <img src={TECH_IMAGE} alt="Technology Integration" className="w-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center"
          >
            <h2 className="section-title mb-4">
              Experience Our <span className="text-gradient">Technology</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mb-8">
              Schedule a personalized demo to see how our platforms can transform your clinical operations
            </p>
            <button 
              onClick={onDemoClick}
              className="btn-gradient px-8 py-4"
              data-testid="technology-cta-btn"
            >
              Schedule a Demo
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
